/**
 * TimelineView - Full timeline screen showing state patterns and task completions
 * 
 * Features:
 * - Horizontal scrolling timeline by day
 * - Toggleable state dimensions (Mood, Focus, Space)
 * - Task completions row
 * - Adjustable history range
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  X, ChevronLeft, ChevronRight, CheckCircle, 
  Smile, Meh, Frown, Annoyed, SmilePlus,
  Brain, Palette, Heart, Users, Footprints, Moon,
  Sofa, Monitor, MapPin
} from 'lucide-react';
import { ABLE_COLORS } from '../../constants';

// Mood icons by level
const MOOD_ICONS = {
  1: Frown,
  2: Annoyed,
  3: Meh,
  4: Smile,
  5: SmilePlus
};

// Focus icons by value
const FOCUS_ICONS = {
  'Creative': Palette,
  'Thinking': Brain,
  'Wellbeing': Heart,
  'Social': Users,
  'Physical': Footprints,
  'Rest': Moon
};

// Location icons by value
const LOCATION_ICONS = {
  'Couch': Sofa,
  'Desk': Monitor,
  'Moving': Footprints
};

// Dimension configuration
const DIMENSIONS = {
  feeling: {
    key: 'feeling',
    label: 'Mood',
    icon: Smile,
    getColor: (value) => ABLE_COLORS.feeling[value] || ABLE_COLORS.textMuted,
    getIcon: (value) => MOOD_ICONS[value] || Meh
  },
  focus: {
    key: 'focus',
    label: 'Focus',
    icon: Brain,
    getColor: (value) => {
      const colors = {
        'Creative': ABLE_COLORS.state.creative,
        'Thinking': ABLE_COLORS.state.mental,
        'Wellbeing': ABLE_COLORS.state.emotional,
        'Social': ABLE_COLORS.state.social,
        'Physical': ABLE_COLORS.state.physical,
        'Rest': ABLE_COLORS.state.rest
      };
      return colors[value] || ABLE_COLORS.textMuted;
    },
    getIcon: (value) => FOCUS_ICONS[value] || Brain
  },
  location: {
    key: 'location',
    label: 'Space',
    icon: MapPin,
    getColor: (value) => {
      const colors = {
        'Couch': ABLE_COLORS.location.couch,
        'Desk': ABLE_COLORS.location.desk,
        'Moving': ABLE_COLORS.location.moving
      };
      return colors[value] || ABLE_COLORS.textMuted;
    },
    getIcon: (value) => {
      if (value?.startsWith('Other:')) return MapPin;
      return LOCATION_ICONS[value] || MapPin;
    }
  }
};

// Range options
const RANGE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' }
];

/**
 * Group entries by date (YYYY-MM-DD)
 */
function groupByDate(entries) {
  const grouped = {};
  entries.forEach(entry => {
    const date = new Date(entry.timestamp).toISOString().split('T')[0];
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(entry);
  });
  return grouped;
}

/**
 * Generate array of dates for the timeline
 */
function generateDateRange(days) {
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
}

/**
 * Format date for display
 */
function formatDate(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (dateStr === today.toISOString().split('T')[0]) return 'Today';
  if (dateStr === yesterday.toISOString().split('T')[0]) return 'Yest.';
  
  return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
}

/**
 * DimensionToggle - Button to toggle a dimension on/off
 */
function DimensionToggle({ dimension, enabled, onToggle, compact = false }) {
  const config = DIMENSIONS[dimension];
  const Icon = config.icon;
  
  return (
    <button
      onClick={() => onToggle(dimension)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? '4px' : '6px',
        padding: compact ? '8px 10px' : '8px 14px',
        background: enabled ? `${ABLE_COLORS.primary}15` : ABLE_COLORS.neutral,
        border: `2px solid ${enabled ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
        borderRadius: ABLE_COLORS.radiusSmall,
        cursor: 'pointer',
        transition: 'all 0.15s ease'
      }}
    >
      <Icon size={compact ? 14 : 16} color={enabled ? ABLE_COLORS.primary : ABLE_COLORS.textMuted} />
      <span style={{
        fontSize: compact ? '12px' : '13px',
        fontWeight: enabled ? '600' : '500',
        color: enabled ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary
      }}>
        {config.label}
      </span>
    </button>
  );
}

/**
 * DayColumn - Single day in the timeline
 */
function DayColumn({ date, stateEntries, taskEntries, enabledDimensions, isToday }) {
  // Get the last state for each dimension on this day
  const getLastState = (dimension) => {
    const entries = stateEntries.filter(e => e[dimension] != null);
    if (entries.length === 0) return null;
    // Sort by timestamp and get most recent
    return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0][dimension];
  };
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minWidth: '72px',
      borderRight: `1px solid ${ABLE_COLORS.borderLight}`,
      background: isToday ? `${ABLE_COLORS.primary}05` : 'transparent'
    }}>
      {/* Date header */}
      <div style={{
        padding: '10px 6px',
        borderBottom: `1px solid ${ABLE_COLORS.borderLight}`,
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        background: isToday ? `${ABLE_COLORS.primary}10` : ABLE_COLORS.neutral,
        zIndex: 1
      }}>
        <span style={{
          fontSize: '12px',
          fontWeight: isToday ? '700' : '500',
          color: isToday ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary
        }}>
          {formatDate(date)}
        </span>
      </div>
      
      {/* State dimension rows */}
      {Object.keys(DIMENSIONS).map(dim => {
        if (!enabledDimensions.includes(dim)) return null;
        
        const config = DIMENSIONS[dim];
        const value = getLastState(dim);
        const color = value != null ? config.getColor(value) : ABLE_COLORS.border;
        const Icon = value != null ? config.getIcon(value) : null;
        
        return (
          <div
            key={dim}
            style={{
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${ABLE_COLORS.borderLight}`
            }}
          >
            {value != null ? (
              <div
                title={`${config.label}: ${value}`}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: `${color}20`,
                  border: `2px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {Icon && <Icon size={16} color={color} strokeWidth={2.5} />}
              </div>
            ) : (
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: ABLE_COLORS.border
              }} />
            )}
          </div>
        );
      })}
      
      {/* Tasks row */}
      <div style={{
        minHeight: '56px',
        padding: '8px 4px',
        display: 'flex',
        flexDirection: 'column',
        gap: '3px'
      }}>
        {taskEntries.length === 0 ? (
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '11px', color: ABLE_COLORS.textMuted }}>-</span>
          </div>
        ) : (
          taskEntries.slice(0, 3).map((task, idx) => (
            <div
              key={task.id || idx}
              title={task.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 5px',
                background: `${ABLE_COLORS.semantic.success}12`,
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <CheckCircle size={10} color={ABLE_COLORS.semantic.success} style={{ flexShrink: 0 }} />
              <span style={{
                fontSize: '10px',
                color: ABLE_COLORS.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {task.title}
              </span>
            </div>
          ))
        )}
        {taskEntries.length > 3 && (
          <span style={{
            fontSize: '10px',
            color: ABLE_COLORS.textMuted,
            textAlign: 'center'
          }}>
            +{taskEntries.length - 3} more
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * EmptyStateMessage - Shown when no dimensions are selected
 */
function EmptyStateMessage() {
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      textAlign: 'center'
    }}>
      <Smile size={40} color={ABLE_COLORS.border} style={{ marginBottom: '16px' }} />
      <p style={{
        margin: 0,
        fontSize: '15px',
        color: ABLE_COLORS.textSecondary,
        maxWidth: '280px',
        lineHeight: 1.5
      }}>
        Pick something to track above to see your patterns over time.
      </p>
    </div>
  );
}

/**
 * TimelineView - Main timeline component
 */
export default function TimelineView({ isOpen, onClose, entries = [] }) {
  const [range, setRange] = useState(7);
  const [enabledDimensions, setEnabledDimensions] = useState(['feeling', 'focus', 'location']);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 500);
  const scrollRef = useRef(null);
  
  // Track window size for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 500);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Generate date range
  const dates = useMemo(() => generateDateRange(range), [range]);
  const today = new Date().toISOString().split('T')[0];
  
  // Group entries by date
  const groupedEntries = useMemo(() => groupByDate(entries), [entries]);
  
  // Separate state and task entries
  const getStateEntries = (date) => {
    const dayEntries = groupedEntries[date] || [];
    return dayEntries.filter(e => e.type === 'state_checkin');
  };
  
  const getTaskEntries = (date) => {
    const dayEntries = groupedEntries[date] || [];
    return dayEntries.filter(e => e.type === 'task_completed');
  };
  
  // Toggle dimension - allow deselecting all
  const toggleDimension = (dim) => {
    setEnabledDimensions(prev => {
      if (prev.includes(dim)) {
        return prev.filter(d => d !== dim);
      }
      return [...prev, dim];
    });
  };
  
  // Scroll to today on mount
  useEffect(() => {
    if (isOpen && scrollRef.current && enabledDimensions.length > 0) {
      // Scroll to the end (today)
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [isOpen, range, enabledDimensions.length]);
  
  // Scroll controls
  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -200 : 200;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };
  
  if (!isOpen) return null;
  
  // Count stats
  const totalTasks = entries.filter(e => e.type === 'task_completed').length;
  const totalCheckins = entries.filter(e => e.type === 'state_checkin').length;
  
  const showTimeline = enabledDimensions.length > 0;
  
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: isMobile ? '16px 16px 0 0' : ABLE_COLORS.cardRadius,
        width: '100%',
        maxWidth: isMobile ? '100%' : '800px',
        maxHeight: isMobile ? '95vh' : '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        overflow: 'hidden',
        position: isMobile ? 'fixed' : 'relative',
        bottom: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 'auto',
        right: isMobile ? 0 : 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px',
          borderBottom: `1px solid ${ABLE_COLORS.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: ABLE_COLORS.textPrimary
            }}>
              Timeline
            </h2>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '13px',
              color: ABLE_COLORS.textMuted
            }}>
              {totalTasks} tasks · {totalCheckins} check-ins
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} color={ABLE_COLORS.textMuted} />
          </button>
        </div>
        
        {/* Controls */}
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderBottom: `1px solid ${ABLE_COLORS.borderLight}`,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          {/* Dimension toggles */}
          <div style={{ 
            display: 'flex', 
            gap: '6px', 
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            {Object.keys(DIMENSIONS).map(dim => (
              <DimensionToggle
                key={dim}
                dimension={dim}
                enabled={enabledDimensions.includes(dim)}
                onToggle={toggleDimension}
                compact={isMobile}
              />
            ))}
          </div>
          
          {/* Range selector */}
          <div style={{ 
            display: 'flex', 
            gap: '6px',
            justifyContent: isMobile ? 'center' : 'flex-end'
          }}>
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setRange(opt.value)}
                style={{
                  padding: isMobile ? '8px 14px' : '6px 12px',
                  background: range === opt.value ? ABLE_COLORS.primary : 'transparent',
                  color: range === opt.value ? 'white' : ABLE_COLORS.textSecondary,
                  border: `1px solid ${range === opt.value ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusSmall,
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Timeline content or empty state */}
        {!showTimeline ? (
          <EmptyStateMessage />
        ) : (
          <>
            {/* Row labels + timeline */}
            <div style={{
              display: 'flex',
              flex: 1,
              overflow: 'hidden'
            }}>
              {/* Fixed label column */}
              <div style={{
                width: isMobile ? '56px' : '72px',
                flexShrink: 0,
                borderRight: `1px solid ${ABLE_COLORS.border}`,
                background: ABLE_COLORS.bgSubtle
              }}>
                {/* Empty header cell */}
                <div style={{
                  height: '38px',
                  borderBottom: `1px solid ${ABLE_COLORS.borderLight}`
                }} />
                
                {/* Dimension labels */}
                {Object.keys(DIMENSIONS).map(dim => {
                  if (!enabledDimensions.includes(dim)) return null;
                  const config = DIMENSIONS[dim];
                  const Icon = config.icon;
                  
                  return (
                    <div
                      key={dim}
                      style={{
                        height: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '0 10px',
                        borderBottom: `1px solid ${ABLE_COLORS.borderLight}`
                      }}
                    >
                      <Icon size={14} color={ABLE_COLORS.textMuted} />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: ABLE_COLORS.textSecondary
                      }}>
                        {config.label}
                      </span>
                    </div>
                  );
                })}
                
                {/* Tasks label */}
                <div style={{
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '0 10px'
                }}>
                  <CheckCircle size={14} color={ABLE_COLORS.semantic.success} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: ABLE_COLORS.textSecondary
                  }}>
                    Tasks
                  </span>
                </div>
              </div>
              
              {/* Scrollable timeline */}
              <div
                ref={scrollRef}
                style={{
                  flex: 1,
                  overflowX: 'auto',
                  display: 'flex'
                }}
              >
                {dates.map(date => (
                  <DayColumn
                    key={date}
                    date={date}
                    stateEntries={getStateEntries(date)}
                    taskEntries={getTaskEntries(date)}
                    enabledDimensions={enabledDimensions}
                    isToday={date === today}
                  />
                ))}
              </div>
            </div>
            
            {/* Scroll controls */}
            <div style={{
              padding: '14px 20px',
              borderTop: `1px solid ${ABLE_COLORS.borderLight}`,
              display: 'flex',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <button
                onClick={() => scroll('left')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 18px',
                  background: ABLE_COLORS.bgSubtle,
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusMedium,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary
                }}
              >
                <ChevronLeft size={18} />
                Earlier
              </button>
              <button
                onClick={() => scroll('right')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 18px',
                  background: ABLE_COLORS.bgSubtle,
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusMedium,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary
                }}
              >
                Later
                <ChevronRight size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
